import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon, Inbox as InboxIcon } from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';

const STATUSES = ['PENDING', 'APPROVED', 'CLAIMED', 'SUPERSEDED', 'REJECTED'];

// Display only -- ProviderApplicationStatus is unchanged server-side.
//
// PENDING is NOT "not yet invited": ClaimInitiationService creates the application with
// approvedAt = null AND raises a PROVIDER_PUBLIC_APPLICATION moderation submission, which
// ModerationSubmissionService resolves by minting the claim token. So a PENDING row is an
// application awaiting a decision taken in Submissions -- amber, and linked there.
const STATUS_LABELS = {
  PENDING: 'Awaiting review',
  APPROVED: 'Invited, awaiting claim',
  CLAIMED: 'Claimed',
  SUPERSEDED: 'Superseded',
  REJECTED: 'Closed',
};

const STATUS_COLORS = {
  PENDING: 'warning',
  // 'default', not 'info': waiting on the provider is not a second attention state.
  APPROVED: 'default',
  CLAIMED: 'success',
  REJECTED: 'error',
  SUPERSEDED: 'default',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const displayOrDash = (value) => (value === null || value === undefined || value === '' ? '—' : value);

const StatusChip = ({ status }) => (
  <Chip size="small" label={STATUS_LABELS[status] ?? status} color={STATUS_COLORS[status] || 'default'} />
);

const FieldRow = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
    <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" sx={{ mb: 1.5, mt: 2 }}>{children}</Typography>
);

const ProviderApplications = () => {
  const navigate = useNavigate();
  const api = useApi();

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selected, setSelected] = useState(null);
  const [detailError, setDetailError] = useState('');

  // Link-to-user dialog state
  const [claimForUserOpen, setClaimForUserOpen] = useState(false);
  const [claimForUserId, setClaimForUserId] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.providerApplication.list({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        page,
        size: rowsPerPage,
      });
      setRows(data?.content || []);
      setTotalElements(data?.totalElements || 0);
      setError('');
    } catch (e) {
      console.error(e);
      setError('Failed to load provider applications.');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleStatusFilterChange = (_, value) => {
    if (value === null) return; // ignore re-clicking the active toggle
    setStatusFilter(value);
    setPage(0);
  };

  // The list already returns the complete ProviderApplicationAdminDto, so the row is
  // rendered immediately and the re-fetch only refreshes it in place. No spinner: it
  // would cover data we already have. If the re-fetch fails the row stays visible and
  // the error says so, rather than replacing good-but-slightly-stale data with nothing.
  const openDetail = async (row) => {
    setSelected(row);
    setDetailError('');
    try {
      const fresh = await api.providerApplication.get(row.id);
      setSelected(fresh);
    } catch (e) {
      console.error(e);
      setDetailError('Could not refresh this application; showing the values from the list.');
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetailError('');
    setClaimForUserOpen(false);
    setClaimForUserId('');
  };

  const refreshSelected = async (id) => {
    try {
      const fresh = await api.providerApplication.get(id);
      setSelected(fresh);
    } catch (e) {
      console.error(e);
    }
  };

  const handleResend = async () => {
    if (!selected) return;
    setActionBusy(true);
    try {
      await api.providerApplication.resendClaimLink(selected.id);
      flashSuccess('A new claim link has been sent. Any earlier link no longer works.');
      await refreshSelected(selected.id);
      await fetchRows();
    } catch (e) {
      console.error(e);
      setDetailError('Could not send the claim email. The previous link is unchanged.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleClaimForUser = async () => {
    if (!selected || !claimForUserId) return;
    setActionBusy(true);
    try {
      await api.providerApplication.claimForUser(selected.id, Number(claimForUserId));
      flashSuccess('Listing linked to that user.');
      setClaimForUserOpen(false);
      setClaimForUserId('');
      await refreshSelected(selected.id);
      await fetchRows();
    } catch (e) {
      console.error(e);
      setDetailError('Could not link the listing. Check the user id and that they do not already have one.');
    } finally {
      setActionBusy(false);
    }
  };

  const showActions = selected?.status === 'APPROVED';

  const contactEmailNote = useMemo(() => {
    if (!selected) return null;
    if (!selected.contactEmailNormalized) return null;
    if (selected.contactEmailNormalized === selected.contactEmail) return null;
    return selected.contactEmailNormalized;
  }, [selected]);

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">Provider Claims</Typography>
        <Typography variant="body2" color="text.secondary">
          Claim invitations and their outcomes. Approve and reject happen in Submissions — this
          page mirrors that queue rather than owning it.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={handleStatusFilterChange}
            size="small"
          >
            <ToggleButton value="ALL">All</ToggleButton>
            {STATUSES.map((s) => (
              <ToggleButton key={s} value={s}>{STATUS_LABELS[s] ?? s}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Business</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Services</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box sx={{ p: 5, textAlign: 'center', color: 'text.secondary' }}>
                        <InboxIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                        <Typography>No applications yet.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(row)}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap>{row.businessName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>{row.contactName}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {row.contactEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>{displayOrDash(row.city)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                          {(row.serviceTypes || []).length > 0
                            ? row.serviceTypes.map((s) => <Chip key={s} label={s} size="small" variant="outlined" />)
                            : '—'}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {row.status === 'PENDING' ? (
                          <Button
                            size="small"
                            color="warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/submissions?tab=PROVIDER_PUBLIC_APPLICATION');
                            }}
                          >
                            {STATUS_LABELS.PENDING}
                          </Button>
                        ) : <StatusChip status={row.status} />}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.createdAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Detail dialog */}
      <Dialog open={selected !== null} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle>Application Details</DialogTitle>
        <DialogContent>
          {selected ? (
            <Box>
              {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}

              <SectionTitle>Business</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><FieldRow label="Business name" value={displayOrDash(selected.businessName)} /></Grid>
                <Grid item xs={12} sm={6}><FieldRow label="City" value={displayOrDash(selected.city)} /></Grid>
                <Grid item xs={12}>
                  <FieldRow
                    label="Services"
                    value={(selected.serviceTypes || []).length > 0 ? (selected.serviceTypes || []).join(', ') : '—'}
                  />
                </Grid>
                <Grid item xs={12}><FieldRow label="About" value={displayOrDash(selected.about)} /></Grid>
              </Grid>

              <Divider sx={{ mt: 1 }} />
              <SectionTitle>Contact</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><FieldRow label="Contact name" value={displayOrDash(selected.contactName)} /></Grid>
                <Grid item xs={12} sm={6}><FieldRow label="Contact phone" value={displayOrDash(selected.contactPhone)} /></Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" component="div">Contact email</Typography>
                  <Typography variant="body2" component="div">{displayOrDash(selected.contactEmail)}</Typography>
                  {contactEmailNote && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      normalized: {contactEmailNote}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Divider sx={{ mt: 1 }} />
              <SectionTitle>Legal acceptance</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FieldRow
                    label="Provider terms"
                    value={selected.acceptedTermsVersion
                      ? `${selected.acceptedTermsVersion} (${selected.acceptedTermsLocale || '?'})`
                      : 'Not recorded'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldRow
                    label="Accepted at"
                    value={selected.acceptedTermsAt ? formatDate(selected.acceptedTermsAt) : '—'}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ mt: 1 }} />
              <SectionTitle>Lifecycle</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" component="div">Status</Typography>
                  <Box sx={{ mt: 0.5 }}><StatusChip status={selected.status} /></Box>
                </Grid>
                <Grid item xs={12} sm={6}><FieldRow label="Promoted provider id" value={displayOrDash(selected.promotedProviderId)} /></Grid>
                <Grid item xs={12} sm={6}><FieldRow label="Created" value={formatDate(selected.createdAt)} /></Grid>
                <Grid item xs={12} sm={6}><FieldRow label="Approved" value={formatDate(selected.approvedAt)} /></Grid>
                <Grid item xs={12} sm={6}><FieldRow label="Claimed" value={formatDate(selected.claimedAt)} /></Grid>
                <Grid item xs={12} sm={6}><FieldRow label="Superseded" value={formatDate(selected.supersededAt)} /></Grid>
                <Grid item xs={12} sm={6}><FieldRow label="Closed" value={formatDate(selected.closedAt)} /></Grid>
                <Grid item xs={12} sm={6}><FieldRow label="Claim link expires" value={formatDate(selected.claimTokenExpiresAt)} /></Grid>
              </Grid>

              <Divider sx={{ mt: 1 }} />
              <SectionTitle>Account matching</SectionTitle>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Advisory only — these are display hints and do not gate anything server-side. Do not treat them as a decision.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><FieldRow label="Matched user id" value={displayOrDash(selected.matchedUserId)} /></Grid>
                <Grid item xs={12} sm={6}>
                  <FieldRow
                    label="Matched user already has a provider"
                    value={selected.matchedUserId ? (selected.matchedUserHasProvider ? 'Yes' : 'No') : '—'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}><FieldRow label="Claimed by user id" value={displayOrDash(selected.claimedByUserId)} /></Grid>
              </Grid>

              <Divider sx={{ mt: 1 }} />
              <SectionTitle>Submission origin</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}><FieldRow label="IP" value={displayOrDash(selected.submittedIp)} /></Grid>
                <Grid item xs={12} sm={4}><FieldRow label="User agent" value={displayOrDash(selected.userAgent)} /></Grid>
                <Grid item xs={12} sm={4}><FieldRow label="Referrer" value={displayOrDash(selected.referrer)} /></Grid>
              </Grid>

              {showActions && (
                <>
                  <Divider sx={{ mt: 1 }} />
                  <SectionTitle>Actions</SectionTitle>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Re-sending always mints a fresh claim link and invalidates the previous one.
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" disabled={actionBusy} onClick={handleResend}>
                      Re-send claim link
                    </Button>
                    <Button variant="outlined" disabled={actionBusy} onClick={() => setClaimForUserOpen(true)}>
                      Link to a user…
                    </Button>
                  </Stack>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Link-to-user dialog */}
      <Dialog open={claimForUserOpen} onClose={() => setClaimForUserOpen(false)}>
        <DialogTitle>Link this listing to a user</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Last resort, for an applicant who signed up under a different address and lost
            their claim link. This creates their provider listing immediately.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="User id"
            value={claimForUserId}
            onChange={(e) => setClaimForUserId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimForUserOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!claimForUserId || actionBusy} onClick={handleClaimForUser}>
            Link listing
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProviderApplications;
